import { Inject, Injectable} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { User } from '../../shared/models';
import { LocalStorageService} from '../../xform-compat';
import { OKTA_AUTH, OktaAuthStateService } from '@okta/okta-angular';
import { AuthState, OktaAuth } from '@okta/okta-auth-js';
import { filter, map } from 'rxjs/operators';
import { CoreService } from '../data-services/core.service';
import { EventsHubService } from '../events-hub.service';

@Injectable()
export class AuthenticationService {

  private isAuthenticated: boolean = false;
  private userEmail: string = '';
  private loggedUser: any;

  constructor(
    private storage: LocalStorageService,
    private translateService: TranslateService,
    private coreService: CoreService,
    private eventsHubService: EventsHubService,
    private oktaStateService: OktaAuthStateService,
    @Inject(OKTA_AUTH) private oktaAuth: OktaAuth,
  ) {
    this.oktaStateService.authState$.pipe(
      filter((s: AuthState) => !!s),
    ).subscribe((s: AuthState) => {
      this.isAuthenticated = s.isAuthenticated ? s.isAuthenticated : false;
      if (this.isAuthenticated) {
        this.userEmail = s.idToken.claims.email;
      }
      this.eventsHubService.isLoggedIn$.next(this.isAuthenticated);
    })
    if (this.isAuthenticated) {
      this.coreService.getLoggedUser().subscribe(user => this.loggedUser = user);
    }
  }

  public async login() {
    await this.oktaAuth.signInWithRedirect().then( () => {
      if (this.isAuthenticated) {
        this.coreService.getLoggedUser().subscribe(user => this.loggedUser = user);
      }
    });
  }

  public async logout() {
    await this.oktaAuth.signOut();
  }

  public  isLoggedIn() {
    return this.isAuthenticated;
  }

  public getUserEmail(): string {
    return this.userEmail;
  }

  public getLoggedUser(): User {
    return this.loggedUser;
  }

  /**
   * Check if the permissions stored have changed
   * @param oldLoggedUserInfo Previous stored user data
   * @param newLoggedUserInfo Refreshed user data
   * @returns {boolean} If there are changes on permissions
   */
  public permissionsChanged(oldLoggedUserInfo, newLoggedUserInfo): boolean {
    if (!oldLoggedUserInfo || !newLoggedUserInfo || oldLoggedUserInfo.superUser !== newLoggedUserInfo.superUser ||
      oldLoggedUserInfo.userTenants[0].administrator !== newLoggedUserInfo.userTenants[0].administrator) {
      return true;
    }
    // Check permission ids
    const oldPermissions = new Set();
    const newPermissions = new Set();
    oldLoggedUserInfo.userTenants[0].tenantRoles.forEach(function (role) {
      role.permissions.forEach(permission => oldPermissions.add(permission.id));
    });
    newLoggedUserInfo.userTenants[0].tenantRoles.forEach(function (role) {
      role.permissions.forEach(permission => newPermissions.add(permission.id));
    });
    // newPermissions = [1,3,4]; oldPermissions = [1,4,3] => "1,3,4" !== "1,3,4"
    return (Array.from(newPermissions).sort().toString() !== Array.from(oldPermissions).sort().toString());
  }

  public setLang(userLang: string, defaultLang?: string) {
    this.storage.set('lang', userLang || defaultLang);
  }

  public getLang(): string {
    return this.translateService.getDefaultLang();
  }

}
